/**
 * Hook to check if user can perform an action on a doctype/document
 * Returns both permission status and loading state for UI gating
 * 
 * Use this hook when you need to show loading states or prevent flash of "Access Denied"
 * For simple boolean checks, use `useCan` instead.
 */

import { useQuery } from "@tanstack/react-query";
import { getERPClient } from "@/lib/erp/client";
import { useAuthStore } from "@/lib/store/authStore";

export type PermissionAction = 
  | "read" 
  | "write" 
  | "print" 
  | "delete" 
  | "submit" 
  | "cancel" 
  | "amend";

interface UseCanQueryResult {
  allowed: boolean;
  isLoading: boolean;
}

export function useCanQuery(
  doctype: string,
  action: PermissionAction,
  name?: string
): UseCanQueryResult {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const { data, isLoading } = useQuery({
    queryKey: ["permission", doctype, action, name],
    queryFn: async () => {
      const client = getERPClient();
      const res = await client.call<{ allowed: boolean }>(
        "zaviago_backend.api.permissions.can",
        { doctype, action, name }
      );
      return res;
    },
    // Cache permissions for 5 minutes (eventually-consistent is OK)
    staleTime: 5 * 60 * 1000,
    // Don't refetch on window focus for permissions (reduce noise)
    refetchOnWindowFocus: false,
    // Only fetch if authenticated and we have required params
    enabled: isAuthenticated && !!doctype && !!action,
  });

  return {
    allowed: data?.allowed ?? false,
    isLoading,
  };
}
